import React, { useState } from 'react';
import { Link } from 'wouter';
import { Input } from '@/components/ui/input';
import { UnifiedCard } from '@/components/ui/UnifiedCard';
import {
  Search,
  Home,
  BookOpen,
  Calendar,
  ShoppingBag,
  ArrowRight,
  Sparkles,
  MapPin,
} from 'lucide-react';
import logoImage from '@assets/logo_1755310245094.png';
import Navigation from '@/components/Navigation';

/**
 * Members-only 404 Not Found page with glass morphism styling.
 * Displays themed 404 with search, quick navigation, and spiritual messaging.
 */
export default function NotFound() {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/courses?search=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  const popularPaths = [
    {
      path: '/dashboard',
      label: 'Your Dashboard',
      description: 'Continue your learning journey',
      icon: Home,
      gradient: 'from-cyan-500 to-blue-500',
      shadowColor: 'rgba(6, 182, 212, 0.3)',
    },
    {
      path: '/courses',
      label: 'Course Gallery',
      description: 'Explore our spiritual teachings',
      icon: BookOpen,
      gradient: 'from-purple-500 to-pink-500',
      shadowColor: 'rgba(168, 85, 247, 0.3)',
    },
    {
      path: '/consultations',
      label: 'Consultation',
      description: 'Book a guidance session',
      icon: Calendar,
      gradient: 'from-amber-500 to-orange-500',
      shadowColor: 'rgba(245, 158, 11, 0.3)',
    },
    {
      path: '/shop',
      label: 'Shop',
      description: 'Browse our apparel store',
      icon: ShoppingBag,
      gradient: 'from-pink-500 to-rose-500',
      shadowColor: 'rgba(236, 72, 153, 0.3)',
    },
  ];

  return (
    <div className="min-h-screen bg-background text-muted-foreground overflow-x-hidden overflow-y-auto">
      
      <Navigation />

      <main className="relative z-10 pt-24">
        <section className="min-h-[calc(100vh-6rem)] flex items-center justify-center px-4 py-12">
          {/* Decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Floating compass */}
            <div
              className="absolute top-20 left-[10%] w-32 h-32 opacity-10 animate-pulse"
              style={{
                background: 'conic-gradient(from 0deg, #06b6d4, transparent, #06b6d4, transparent)',
                borderRadius: '50%',
              }}
            />
            {/* Floating orb */}
            <div
              className="absolute bottom-32 right-[15%] w-48 h-48 opacity-20 blur-3xl"
              style={{
                background: 'radial-gradient(circle, rgba(168, 85, 247, 0.5) 0%, transparent 70%)',
              }}
            />
            {/* Grid pattern */}
            <div
              className="absolute inset-0 opacity-[0.02]"
              style={{
                backgroundImage:
                  'linear-gradient(#06b6d4 1px, transparent 1px), linear-gradient(90deg, #06b6d4 1px, transparent 1px)',
                backgroundSize: '60px 60px',
              }}
            />
          </div>

          <div className="max-w-4xl mx-auto text-center relative">
            <UnifiedCard
              variant="elevated"
              className="relative overflow-hidden border-0"
              style={{
                background:
                  'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.8) 50%, rgba(15, 23, 42, 0.9) 100%)',
                backdropFilter: 'blur(20px)',
                boxShadow:
                  '0 25px 80px rgba(6, 182, 212, 0.15), 0 0 100px rgba(168, 85, 247, 0.1), inset 0 1px 0 rgba(255,255,255,0.1)',
              }}
            >
              {/* Glass highlight effect */}
              <div
                className="absolute inset-0 opacity-30 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, transparent 50%, rgba(168, 85, 247, 0.1) 100%)',
                }}
              />

              <div className="relative p-8 md:p-12">
                {/* Logo */}
                <div className="flex items-center justify-center mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 blur-2xl bg-brite-sand rounded-full" />
                    <img
                      src={logoImage}
                      alt="White Rabbit Academy"
                      className="relative w-28 h-auto md:w-36 object-contain"
                    />
                  </div>
                </div>

                {/* 404 with gradient */}
                <div className="mb-6">
                  <h1
                    className="text-7xl sm:text-8xl md:text-9xl font-black"
                    style={{
                      background: 'linear-gradient(135deg, #06b6d4 0%, #a855f7 50%, #ec4899 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      filter: 'drop-shadow(0 0 30px rgba(6, 182, 212, 0.5))',
                    }}
                  >
                    404
                  </h1>
                </div>

                {/* Main Message */}
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-foreground">
                  You&apos;ve Been Gifted a{' '}
                  <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                    Sacred Pause
                  </span>
                </h2>

                <p className="text-muted-foreground text-base md:text-lg mb-6 max-w-2xl mx-auto leading-relaxed">
                  This wasn&apos;t an accident—you were guided here. In the mystical traditions,
                  there are no coincidences, only divine appointments disguised as detours.
                </p>

                {/* Quote card with glass effect */}
                <div className="mb-10">
                  <div
                    className="relative rounded-[1.3rem] p-6 max-w-lg mx-auto overflow-hidden"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)',
                      border: '1px solid rgba(6, 182, 212, 0.3)',
                      boxShadow:
                        '0 8px 32px rgba(6, 182, 212, 0.2), inset 0 1px 0 rgba(255,255,255,0.1)',
                    }}
                  >
                    <Sparkles className="absolute top-3 right-3 w-5 h-5 text-brite-terra/50" />
                    <p className="text-lg sm:text-xl md:text-2xl font-semibold text-center bg-gradient-to-r from-cyan-300 via-purple-300 to-pink-300 bg-clip-text text-transparent italic">
                      &quot;Chance is but a name for Law not recognized.&quot;
                    </p>
                  </div>
                </div>

                {/* Search */}
                <div className="mb-10">
                  <h3 className="text-muted-foreground text-base font-medium mb-4 flex items-center justify-center gap-2">
                    <MapPin className="w-4 h-4 text-brite-terra" />
                    Light Your Lantern, Find Your Way
                  </h3>
                  <form onSubmit={handleSearch} className="max-w-md mx-auto">
                    <div className="relative group">
                      <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-[1.3rem] opacity-30 blur group-hover:opacity-50 transition-opacity" />
                      <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          type="search"
                          placeholder="Search courses, teachings, or topics..."
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          className="pl-12 h-14 bg-background border-glass-medium text-foreground placeholder:text-muted-foreground focus:ring-cyan-500/50 focus:border-cyan-500 rounded-[1.3rem]"
                          data-testid="search-input"
                        />
                      </div>
                    </div>
                  </form>
                </div>

                {/* Navigation Cards */}
                <div className="mb-8">
                  <h3 className="text-muted-foreground text-base font-medium mb-6">
                    Established Sacred Pathways
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                    {popularPaths.map(item => {
                      const IconComponent = item.icon;
                      return (
                        <Link href={item.path} key={item.path}>
                          <div
                            className="group relative p-4 rounded-[1.3rem] overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                            style={{
                              background:
                                'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              boxShadow: `0 4px 20px ${item.shadowColor}`,
                            }}
                          >
                            {/* Hover glow */}
                            <div
                              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                              style={{
                                background: `linear-gradient(135deg, ${item.shadowColor} 0%, transparent 100%)`,
                              }}
                            />

                            <div className="relative flex items-center gap-4">
                              <div
                                className={`w-12 h-12 rounded-[1rem] bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                              >
                                <IconComponent className="w-6 h-6 text-foreground" />
                              </div>
                              <div className="flex-1 text-left min-w-0">
                                <h4 className="font-semibold text-foreground group-hover:text-cyan-300 transition-colors">
                                  {item.label}
                                </h4>
                                <p className="text-muted-foreground text-sm">{item.description}</p>
                              </div>
                              <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-brite-terra group-hover:translate-x-1 transition-all" />
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </UnifiedCard>
          </div>
        </section>
      </main>
    </div>
  );
}
